// ============================================
// WebGPURenderContext - WebGPU GPU Abstraction
// ============================================

import {
  type GPUBackend,
  type TextureHandle,
  type BufferHandle,
  type PipelineHandle,
  type ShaderHandle,
  type RenderTargetHandle,
  type MRTRenderTargetHandle,
  type ComputePipelineHandle,
  type TextureDescriptor,
  type BufferDescriptor,
  type PipelineDescriptor,
  type ComputePipelineDescriptor,
  type ComputeBindGroup,
  type DispatchSize,
  type RenderStats,
  type BlendMode,
  type VertexBufferLayout,
  type TextureFormat,
  type MRTRenderTargetDescriptor,
  type MRTRenderTargetInfo,
  NULL_HANDLE,
} from "../types";
import {
  type IRenderContext,
  type RenderContextOptions,
} from "./IRenderContext";
import { LIGHT_UNIFORMS } from "../lighting/LightLayouts";
import { GpuTimer } from "./GpuTimer";
import Logger from '@engine/logger';

// Internal resource tracking
interface TextureResource {
  gpuTexture: GPUTexture;
  gpuView: GPUTextureView;
  sampler: GPUSampler;
  width: number;
  height: number;
  format: GPUTextureFormat;
}

interface BufferResource {
  gpuBuffer: GPUBuffer;
  size: number;
  usage: GPUBufferUsageFlags;
}

interface PipelineResource {
  pipeline: GPURenderPipeline;
  bindGroupLayout: GPUBindGroupLayout;
  blendMode: BlendMode;
  usesTextures: boolean;
  textureCount: number;
  uniformLayout: Map<string, { offset: number; size: number }>; // name -> {offset, size}
  hasLighting: boolean;
  hasForwardPlus: boolean;
}

interface ShaderResource {
  module: GPUShaderModule;
}

interface RenderTargetResource {
  colorTexture: TextureHandle;
  colorView: GPUTextureView;
  depthTexture?: TextureHandle;
  depthView?: GPUTextureView;
  width: number;
  height: number;
}

interface MRTRenderTargetResource {
  colorTextures: TextureHandle[];
  colorViews: GPUTextureView[];
  depthTexture?: TextureHandle;
  depthView?: GPUTextureView;
  width: number;
  height: number;
  label?: string;
}

interface ComputePipelineResource {
  pipeline: GPUComputePipeline;
  bindGroupLayout: GPUBindGroupLayout;
  textureBindGroupLayout?: GPUBindGroupLayout;
  workgroupSize: [number, number, number];
  storageBufferCount: number;
  textureCount: number;
}

const log = Logger.create('RenderContext');

/**
 * WebGPU Render Context
 * Manages GPU resources and command execution
 */
export class WebGPURenderContext implements IRenderContext {
  readonly backend: GPUBackend = "webgpu";
  readonly canvas: HTMLCanvasElement;

  /**
   * Get underlying WebGPU device for resource sharing between contexts
   */
  getDevice(): GPUDevice | null {
    return this.device ?? null;
  }

  /**
   * Get internal resource maps for sharing with other contexts.
   * @internal
   */
  getResourceMaps(): {
    textures: Map<TextureHandle, TextureResource>;
    buffers: Map<BufferHandle, BufferResource>;
    pipelines: Map<PipelineHandle, PipelineResource>;
    shaders: Map<ShaderHandle, ShaderResource>;
    renderTargets: Map<RenderTargetHandle, RenderTargetResource>;
    mrtRenderTargets: Map<MRTRenderTargetHandle, MRTRenderTargetResource>;
    computePipelines: Map<ComputePipelineHandle, ComputePipelineResource>;
    handleCounters: {
      texture: number;
      buffer: number;
      pipeline: number;
      shader: number;
      renderTarget: number;
      mrtRenderTarget: number;
      computePipeline: number;
    };
  } {
    return {
      textures: this.textures,
      buffers: this.buffers,
      pipelines: this.pipelines,
      shaders: this.shaders,
      renderTargets: this.renderTargets,
      mrtRenderTargets: this.mrtRenderTargets,
      computePipelines: this.computePipelines,
      handleCounters: this.handleCounters,
    };
  }

  // WebGPU objects
  private adapter!: GPUAdapter;
  private device!: GPUDevice;
  private context!: GPUCanvasContext;
  private presentationFormat!: GPUTextureFormat;

  // Resource pools (may be shared with other contexts)
  private textures: Map<TextureHandle, TextureResource> = new Map();
  private buffers: Map<BufferHandle, BufferResource> = new Map();
  private pipelines: Map<PipelineHandle, PipelineResource> = new Map();
  private shaders: Map<ShaderHandle, ShaderResource> = new Map();
  private renderTargets: Map<RenderTargetHandle, RenderTargetResource> =
    new Map();
  private computePipelines: Map<
    ComputePipelineHandle,
    ComputePipelineResource
  > = new Map();
  private mrtRenderTargets: Map<MRTRenderTargetHandle, MRTRenderTargetResource> = new Map();

  // Handle counters (wrapped in object for sharing between contexts)
  private handleCounters = {
    texture: 1,
    buffer: 1,
    pipeline: 1,
    shader: 1,
    renderTarget: 1,
    mrtRenderTarget: 1,
    computePipeline: 1,
  };

  // Current frame state
  private currentPipeline: PipelineHandle = NULL_HANDLE;
  private currentBindGroups: Map<number, GPUBindGroup> = new Map();
  private currentRenderTarget: RenderTargetHandle = NULL_HANDLE;
  private currentMRTRenderTarget: MRTRenderTargetHandle = NULL_HANDLE;
  private commandEncoder: GPUCommandEncoder | null = null;
  private renderPassEncoder: GPURenderPassEncoder | null = null;
  private computePassEncoder: GPUComputePassEncoder | null = null;
  private currentComputePipeline: ComputePipelineHandle = NULL_HANDLE;

  // Currently bound textures (for bindUniforms to use) - indexed by slot
  private boundTextures: Map<number, TextureHandle> = new Map();

  // Pending uniform data
  private uniformBuffer: GPUBuffer | null = null;
  private dummyStorageBuffers: GPUBuffer[] = []; // One per binding slot to avoid aliasing
  private dummyTexture: TextureResource | null = null; // 1x1 white texture for compute fallback
  private uniformData: Float32Array = new Float32Array(256); // Max 256 floats (4KB)
  private uniformOffset = 0;
  // High-water byte count of setUniform writes pending upload to `uniformBuffer`. setUniform only
  // marks this dirty; the actual (single) writeBuffer is deferred to getSharedUniformBuffer() when a
  // non-snapshot consumer binds the shared buffer — coalescing what used to be one upload PER
  // setUniform (N/draw, all redundant on the snapshot path) into at most one upload per shared-buffer use.
  private uniformDirtyBytes = 0;
  // True when the current group-0 bind references the SHARED uniform buffer (not a per-draw snapshot).
  // Post-process binds the texture group BEFORE setting its uniforms, so the shared buffer must be
  // re-flushed at draw time; the snapshot path must NOT (it would re-add a per-draw write). See draw().
  private sharedUniformBound = false;

  // Per-draw uniform buffer pool (for captureUniforms)
  // Ensures each draw call sees its own uniform snapshot instead of sharing
  // a single buffer that gets overwritten by subsequent setUniform calls.
  private capturedUniformBuffer: GPUBuffer | null = null;
  private uniformBufferPool: GPUBuffer[] = [];
  private uniformPoolIndex = 0;

  // Forward-lit lighting bind group (group 1)
  private lightingBindGroupLayout: GPUBindGroupLayout | null = null;
  private lightingBindGroup: GPUBindGroup | null = null;

  // Forward+ lighting bind group (group 1, with tile data + shadows)
  private forwardPlusBindGroupLayout: GPUBindGroupLayout | null = null;
  private forwardPlusBindGroup: GPUBindGroup | null = null;
  private _dummyShadowBuffer: GPUBuffer | null = null;

  // ── Group-0 bind-group cache ──
  // A bind group references its buffers/texture-views by identity, not by contents, and every
  // resource handle here is monotonic (never reused). So a (pipeline, uniform-buffer, textures) key
  // can never false-hit — the same key always describes an equivalent bind group, and per-frame
  // writeBuffer into a cached group's uniform buffer stays valid. Cleared wholesale on any resource
  // destroy or on overflow (correctness is guaranteed by monotonic handles; this is memory hygiene).
  private bindGroupCache = new Map<string, GPUBindGroup>();
  private bindGroupBufferIds = new WeakMap<GPUBuffer, number>();
  private _bindGroupBufferIdNext = 1;
  private static readonly BIND_GROUP_CACHE_CAP = 512;
  /** Input-handle keys of the last group-1 lighting / forward+ bind groups, to skip identical rebuilds. */
  private _lightingBindGroupKey = '';
  private _forwardPlusBindGroupKey = '';
  private _dummyIESBuffer: GPUBuffer | null = null;
  // Safety-net Forward+ group for lit materials drawn with no real light data bound (no subsystem / cull not
  // ready). Degrades to ambient-only (white ambient ⇒ shows albedo) instead of a missing-bind-group crash.
  private _emptyForwardPlusBindGroup: GPUBindGroup | null = null;
  private _dummyLightUniformBuffer: GPUBuffer | null = null;
  private _dummyLightStorageBuffer: GPUBuffer | null = null;

  // Stats
  private stats: RenderStats = {
    drawCalls: 0,
    triangles: 0,
    vertices: 0,
    textureBinds: 0,
    pipelineChanges: 0,
    bufferUploads: 0,
    frameTime: 0,
    computeDispatches: 0,
    bindGroupsCreated: 0,
    queueSubmits: 0,
    vramUsed: 0,
    vramAllocated: 0,
    textureCount: 0,
    bufferCount: 0,
  };
  
  // VRAM tracking (accumulated)
  private vramAllocated = 0;
  private vramTotal = 0; // From adapter.info.memoryHeaps

  // Real GPU timing via timestamp queries (null when unsupported/disabled).
  private gpuTimer: GpuTimer | null = null;
  /** Marker set by the render pipeline so GPU passes are attributed to a section. */
  private _gpuLabel: string | null = null;

  private initialized = false;

  /** Alpha mode chosen at init; reused on every reconfigure so resize can't silently flip it. */
  private configuredAlphaMode: GPUCanvasAlphaMode = "opaque";

  private constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
  }

  /**
   * Create WebGPU context (async initialization required)
   */
  static async create(
    options: RenderContextOptions,
  ): Promise<WebGPURenderContext> {
    const ctx = new WebGPURenderContext(options.canvas);
    await ctx.init(options);
    return ctx;
  }

  private async init(options: RenderContextOptions): Promise<void> {
    if (!navigator.gpu) {
      throw new Error("WebGPU not supported");
    }

    // Check if we should share resources from another context
    const sharedCtx = options.sharedContext as WebGPURenderContext | undefined;
    if (sharedCtx) {
      // Share the device
      this.device = sharedCtx.device;
      this.adapter = sharedCtx.adapter;

      // Share resource maps AND handle counters - handles created on source context will work here
      const maps = sharedCtx.getResourceMaps();
      this.textures = maps.textures;
      this.buffers = maps.buffers;
      this.pipelines = maps.pipelines;
      this.shaders = maps.shaders;
      this.renderTargets = maps.renderTargets;
      this.mrtRenderTargets = maps.mrtRenderTargets;
      this.computePipelines = maps.computePipelines;
      this.handleCounters = maps.handleCounters;

      console.log("Sharing resources with source context");
    } else if (options.sharedDevice) {
      // Use shared device if provided, otherwise create new one
      this.device = options.sharedDevice;
      // Get adapter from device's limits (can't retrieve original adapter)
      this.adapter = null as unknown as GPUAdapter;
    } else {
      const adapter = await navigator.gpu.requestAdapter({
        powerPreference:
          options.powerPreference === "low-power"
            ? "low-power"
            : "high-performance",
      });

      if (!adapter) {
        throw new Error("Failed to get WebGPU adapter");
      }

      this.adapter = adapter;
      
      // Get VRAM info from adapter (Dawn unsafe API). memoryHeaps lists ALL heaps
      // — device-local (dedicated VRAM) AND host-visible (system RAM shared with
      // the GPU). Summing them all reported e.g. 163 GB on a 12 GB card. Dedicated
      // VRAM = the device-local heaps only (Dawn HeapProperty.DeviceLocal = 0x1).
      const info = adapter.info as GPUAdapterInfo & { memoryHeaps?: readonly { size: number; properties: number }[] };
      if (info.memoryHeaps && info.memoryHeaps.length > 0) {
        const DEVICE_LOCAL = 0x1;
        const deviceLocal = info.memoryHeaps.filter((h) => (h.properties & DEVICE_LOCAL) !== 0);
        this.vramTotal = deviceLocal.length > 0
          ? deviceLocal.reduce((sum, h) => sum + h.size, 0)
          : Math.max(...info.memoryHeaps.map((h) => h.size)); // fallback: largest heap
        log.info(`Adapter: ${info.device || info.description}, dedicated VRAM: ${(this.vramTotal / (1024 * 1024 * 1024)).toFixed(2)} GB`);
      }
      
      this.device = await adapter.requestDevice({
        label: "WebGPURenderContext Device",
        requiredFeatures: [
          "core-features-and-limits",
          "depth-clip-control",
          "depth32float-stencil8",
          "timestamp-query",
          "indirect-first-instance",
          "shader-f16",
          "rg11b10ufloat-renderable",
          "bgra8unorm-storage",
          "float32-filterable",
          "float32-blendable",
          "clip-distances",
          "dual-source-blending",
          "subgroups",
          "texture-formats-tier1",
          "primitive-index"
        ],
        requiredLimits: {
          maxTextureDimension1D: 8192,
          maxTextureDimension2D: 8192,
          maxTextureDimension3D: 2048,
          maxTextureArrayLayers: 2048,
          maxBindGroups: 4,
          maxStorageBufferBindingSize: 512 * 1024 * 1024, // 512MB
          maxBufferSize: 512 * 1024 * 1024, // 512MB  
        }
      });

      // Handle device loss
      this.device.lost.then((info) => {
        log.fatal(
          "Device lost:",
          info.message,
          "Reason:",
          info.reason,
        );
        this.initialized = false;
      });

      // Enable error tracking in development
      this.device.onuncapturederror = (event) => {
        console.error("Uncaptured error:", event.error);
      };
    }

    const context = this.canvas.getContext("webgpu");
    if (!context) {
      throw new Error("Failed to get WebGPU context");
    }

    this.context = context;
    this.presentationFormat = navigator.gpu.getPreferredCanvasFormat();

    this.configuredAlphaMode = options.alpha ? "premultiplied" : "opaque";
    this.context.configure({
      device: this.device,
      format: this.presentationFormat,
      alphaMode: this.configuredAlphaMode,
      // COPY_SRC: UI backdrop blur snapshots the backbuffer (copyBackbufferToTexture)
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_DST | GPUTextureUsage.COPY_SRC,
    });

    // Real GPU timing (best-effort; null when timestamp-query is unavailable).
    this.gpuTimer = GpuTimer.tryCreate(this.device);

    // Create uniform buffer (each context needs its own for separate draw calls)
    this.uniformBuffer = this.device.createBuffer({
      size: 4096, // 4KB
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    // Create separate dummy storage buffers for padding compute shader bindings
    // Need separate buffers to avoid aliasing errors when multiple slots need padding
    for (let i = 0; i < 3; i++) {
      this.dummyStorageBuffers.push(
        this.device.createBuffer({
          size: 64, // Small buffer for padding
          usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
          label: `dummy-storage-${i}`,
        }),
      );
    }

    // Create a 1x1 white dummy texture for compute shader fallback
    const dummyTexture = this.device.createTexture({
      size: { width: 1, height: 1 },
      format: 'rgba8unorm',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      label: 'dummy-texture',
    });
    // Write white pixel data
    this.device.queue.writeTexture(
      { texture: dummyTexture },
      new Uint8Array([255, 255, 255, 255]),
      { bytesPerRow: 4 },
      { width: 1, height: 1 },
    );
    const dummyView = dummyTexture.createView();
    const dummySampler = this.device.createSampler({
      magFilter: 'nearest',
      minFilter: 'nearest',
    });
    this.dummyTexture = {
      gpuTexture: dummyTexture,
      gpuView: dummyView,
      sampler: dummySampler,
      width: 1,
      height: 1,
      format: 'rgba8unorm',
    };

    this.initialized = true;
  }

  // ============================================
  // Resource Creation
  // ============================================

  createTexture(desc: TextureDescriptor): TextureHandle {
    const handle = this.handleCounters.texture++;

    const format = this.mapTextureFormat(desc.format ?? "rgba8unorm");
    const usage = this.mapTextureUsage(desc.usage ?? ["sampled"]);

    const texture = this.device.createTexture({
      size: { width: desc.width, height: desc.height },
      format,
      usage,
      mipLevelCount: desc.mipmaps
        ? Math.floor(Math.log2(Math.max(desc.width, desc.height))) + 1
        : 1,
      label: desc.label,
    });

    const view = texture.createView();

    const sampler = this.device.createSampler({
      magFilter: desc.filter === "nearest" ? "nearest" : "linear",
      minFilter: desc.filter === "nearest" ? "nearest" : "linear",
      mipmapFilter: desc.mipmaps ? "linear" : "nearest",
      addressModeU: this.mapWrapMode(desc.wrapU ?? "clamp"),
      addressModeV: this.mapWrapMode(desc.wrapV ?? "clamp"),
    });

    this.textures.set(handle, {
      gpuTexture: texture,
      gpuView: view,
      sampler,
      width: desc.width,
      height: desc.height,
      format,
    });

    // Track VRAM usage (approximate: width * height * bytes per pixel)
    const bytesPerPixel = this.getBytesPerPixel(format);
    const mipLevels = desc.mipmaps ? Math.floor(Math.log2(Math.max(desc.width, desc.height))) + 1 : 1;
    // Mip chain uses ~1.33x base size
    const mipMultiplier = desc.mipmaps ? 1.333 : 1;
    this.vramAllocated += Math.ceil(desc.width * desc.height * bytesPerPixel * mipMultiplier);

    return handle;
  }

  createTextureFromImage(
    image: TexImageSource,
    options?: Partial<TextureDescriptor>,
  ): TextureHandle {
    let width = 0;
    let height = 0;

    if (
      image instanceof HTMLImageElement ||
      image instanceof HTMLCanvasElement ||
      image instanceof HTMLVideoElement
    ) {
      width = image.width;
      height = image.height;
    } else if (image instanceof ImageBitmap || image instanceof ImageData) {
      width = image.width;
      height = image.height;
    }

    // A not-yet-loaded HTMLImageElement reports 0×0; a zero-sized texture is a WebGPU
    // validation error. Clamp to 1×1 so we return a usable handle instead of crashing.
    if (width <= 0 || height <= 0) {
      log.warn(`createTextureFromImage: image has zero dimensions (${width}×${height}), clamping to 1×1`);
      width = Math.max(1, width);
      height = Math.max(1, height);
    }

    const handle = this.createTexture({
      width,
      height,
      format: options?.sRGB ? "rgba8unorm-srgb" : "rgba8unorm",
      usage: ["sampled", "copy-dst", "render-target"], // WebGPU copyExternalImageToTexture requires both COPY_DST and RENDER_ATTACHMENT
      ...options,
    });

    const resource = this.textures.get(handle)!;

    // Copy image to texture
    if (image instanceof ImageBitmap) {
      this.device.queue.copyExternalImageToTexture(
        { source: image },
        { texture: resource.gpuTexture },
        { width, height },
      );
    } else {
      // Convert to ImageBitmap first. The handle is returned synchronously, so callers
      // may sample this texture for one or more frames before the upload lands.
      createImageBitmap(image as ImageBitmapSource)
        .then((bitmap) => {
          // The texture may have been destroyed while the decode was in flight.
          if (!this.textures.has(handle)) return;
          this.device.queue.copyExternalImageToTexture(
            { source: bitmap },
            { texture: resource.gpuTexture },
            { width, height },
          );
        })
        .catch((err) => {
          log.warn("createTextureFromImage: failed to decode image", err);
        });
    }

    return handle;
  }

  updateTexture(
    handle: TextureHandle,
    data: ArrayBufferView | TexImageSource,
    x = 0,
    y = 0,
    width?: number,
    height?: number,
  ): void {
    const resource = this.textures.get(handle);
    if (!resource) return;

    const w = width ?? resource.width;
    const h = height ?? resource.height;

    if (data instanceof Uint8Array || data instanceof Uint8ClampedArray) {
      this.device.queue.writeTexture(
        { texture: resource.gpuTexture, origin: { x, y } },
        data.buffer as ArrayBuffer,
        { offset: data.byteOffset, bytesPerRow: w * 4 },
        { width: w, height: h },
      );
    } else if (data instanceof Float32Array) {
      // Float32 support for rgba32float textures (16 bytes per pixel)
      this.device.queue.writeTexture(
        { texture: resource.gpuTexture, origin: { x, y } },
        data.buffer as ArrayBuffer,
        { offset: data.byteOffset, bytesPerRow: w * 16 },
        { width: w, height: h },
      );
    } else if (
      (typeof ImageBitmap !== "undefined" && data instanceof ImageBitmap) ||
      (typeof HTMLCanvasElement !== "undefined" && data instanceof HTMLCanvasElement) ||
      (typeof OffscreenCanvas !== "undefined" && data instanceof OffscreenCanvas)
    ) {
      // TexImageSource (ImageBitmap / canvas) upload — used to be a silent no-op, which left the
      // texture black (broke ALL MSDF text + UI images). Requires the target to have COPY_DST +
      // RENDER_ATTACHMENT usage (createTextureFromImage sets these; prefer it for one-shot uploads).
      this.device.queue.copyExternalImageToTexture(
        { source: data },
        { texture: resource.gpuTexture, origin: { x, y } },
        { width: w, height: h },
      );
    }
  }

  createBuffer(desc: BufferDescriptor): BufferHandle {
    const handle = this.handleCounters.buffer++;

    const usage = this.mapBufferUsage(desc.usage);

    const buffer = this.device.createBuffer({
      size: desc.size,
      usage,
      mappedAtCreation: desc.data !== undefined,
      label: desc.label,
    });

    if (desc.data) {
      const mapped = new Uint8Array(buffer.getMappedRange());
      const source =
        desc.data instanceof ArrayBuffer
          ? new Uint8Array(desc.data)
          : new Uint8Array(
              desc.data.buffer,
              desc.data.byteOffset,
              desc.data.byteLength,
            );
      mapped.set(source);
      buffer.unmap();
    }

    this.buffers.set(handle, {
      gpuBuffer: buffer,
      size: desc.size,
      usage,
    });

    // Track VRAM usage
    this.vramAllocated += desc.size;

    return handle;
  }

  updateBuffer(handle: BufferHandle, data: ArrayBufferView, offset = 0): void {
    const resource = this.buffers.get(handle);
    if (!resource) return;

    this.device.queue.writeBuffer(
      resource.gpuBuffer,
      offset,
      data.buffer as ArrayBuffer,
      data.byteOffset,
      data.byteLength,
    );
    this.stats.bufferUploads++;
  }

  createShader(vertexSource: string, fragmentSource: string): ShaderHandle {
    const handle = this.handleCounters.shader++;

    // Combine sources (WebGPU uses single WGSL module for both stages)
    const source =
      vertexSource === fragmentSource
        ? vertexSource
        : `${vertexSource}\n${fragmentSource}`;

    const module = this.device.createShaderModule({
      code: source,
    });

    // Check for compilation errors asynchronously
    module.getCompilationInfo().then((info) => {
      for (const msg of info.messages) {
        if (msg.type === "error") {
          log.error(
            `Shader compilation error: ${msg.message} at line ${msg.lineNum}`,
          );
        } else if (msg.type === "warning") {
          log.warn(`Shader warning: ${msg.message}`);
        }
      }
    });

    this.shaders.set(handle, { module });

    return handle;
  }

  createPipeline(desc: PipelineDescriptor): PipelineHandle {
    const handle = this.handleCounters.pipeline++;

    const shader = this.shaders.get(desc.shader);
    if (!shader) throw new Error("Invalid shader handle");

    // Check if pipeline uses textures (default true for backwards compat)
    const usesTextures = desc.primitive?.usesTextures !== false;
    const textureCount = desc.primitive?.textureCount ?? 1;

    // Create bind group layout based on whether textures are used
    let bindGroupLayout: GPUBindGroupLayout;
    if (usesTextures) {
      // Build entries: uniform buffer, sampler, then N textures
      const entries: GPUBindGroupLayoutEntry[] = [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          sampler: { type: "filtering" },
        },
      ];
      // Add texture entries starting at binding 2
      for (let i = 0; i < textureCount; i++) {
        entries.push({
          binding: 2 + i,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "float" },
        });
      }
      bindGroupLayout = this.device.createBindGroupLayout({ entries });
    } else {
      bindGroupLayout = this.device.createBindGroupLayout({
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
            buffer: { type: "uniform" },
          },
        ],
      });
    }

    const hasLighting = desc.hasLighting ?? false;
    const hasForwardPlus = desc.hasForwardPlus ?? false;

    const pipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts: hasForwardPlus
        ? [bindGroupLayout, this.getForwardPlusBindGroupLayout()]
        : hasLighting
        ? [bindGroupLayout, this.getLightingBindGroupLayout()]
        : [bindGroupLayout],
    });

    // Map vertex buffers
    const vertexBuffers: GPUVertexBufferLayout[] = desc.vertexBuffers.map(
      (layout, index) => ({
        arrayStride: layout.stride,
        stepMode: layout.stepMode === "instance" ? "instance" : "vertex",
        attributes: layout.attributes.map((attr) => ({
          format: this.mapVertexFormat(attr.format),
          offset: attr.offset,
          shaderLocation: attr.location,
        })),
      }),
    );

    // Map color targets. blend semantics: null → no blending; a BlendState → mapped;
    // undefined → back-compat default (standard alpha blending).
    const colorTargetStates: GPUColorTargetState[] = desc.colorTargets.map(
      (target) => {
        const b = target.blend;
        const blend: GPUBlendState | undefined =
          b === null
            ? undefined
            : b
            ? {
                color: {
                  srcFactor: this.mapBlendFactor(b.color.srcFactor),
                  dstFactor: this.mapBlendFactor(b.color.dstFactor),
                  operation: this.mapBlendOp(b.color.operation),
                },
                alpha: {
                  srcFactor: this.mapBlendFactor(b.alpha.srcFactor),
                  dstFactor: this.mapBlendFactor(b.alpha.dstFactor),
                  operation: this.mapBlendOp(b.alpha.operation),
                },
              }
            : {
                color: {
                  srcFactor: "src-alpha",
                  dstFactor: "one-minus-src-alpha",
                  operation: "add",
                },
                alpha: {
                  srcFactor: "one",
                  dstFactor: "one-minus-src-alpha",
                  operation: "add",
                },
              };
        return {
          format: this.mapTextureFormat(target.format),
          blend,
          writeMask: target.writeMask ?? GPUColorWrite.ALL,
        };
      },
    );

    // Map override constants: boolean → 0/1 for WebGPU
    let gpuConstants: Record<string, number> | undefined;
    if (desc.constants) {
      gpuConstants = {};
      for (const [key, val] of Object.entries(desc.constants)) {
        gpuConstants[key] = typeof val === 'boolean' ? (val ? 1 : 0) : val;
      }
    }

    let pipeline: GPURenderPipeline;
    try {
      pipeline = this.device.createRenderPipeline({
        layout: pipelineLayout,
        vertex: {
          module: shader.module,
          entryPoint: desc.vertexEntryPoint ?? "vs_main",
          buffers: vertexBuffers,
          constants: gpuConstants,
        },
        fragment: {
          module: shader.module,
          entryPoint: desc.fragmentEntryPoint ?? "fs_main",
          targets: colorTargetStates,
          constants: gpuConstants,
        },
        primitive: {
          topology: this.mapTopology(
            desc.primitive?.topology ?? "triangle-list",
          ),
          cullMode: this.mapCullMode(desc.primitive?.cullMode ?? "none"),
          frontFace: desc.primitive?.frontFace === "cw" ? "cw" : "ccw",
        },
        depthStencil: desc.depthStencil
          ? {
              format: this.mapTextureFormat(desc.depthStencil.format),
              depthWriteEnabled: desc.depthStencil.depthWrite,
              depthCompare: this.mapCompareFunction(
                desc.depthStencil.depthCompare,
              ),
            }
          : undefined,
        label: desc.label,
      });
    } catch (e) {
      log.error(`Failed to create pipeline "${desc.label}":`, e);
      throw e;
    }

    // Build uniform layout from struct definition or fall back to default convention
    const uniformLayout = new Map<string, { offset: number; size: number }>();
    if (desc.uniformDef) {
      for (const field of desc.uniformDef.fields) {
        // offset stored in float32 units (field.offset is bytes, / 4 for f32 index)
        uniformLayout.set(field.name, { offset: field.offset / 4, size: field.type.components });
      }
    } else {
      // Default: uViewProjection at offset 0 (16 floats = 64 bytes), uTime at offset 16
      uniformLayout.set("uViewProjection", { offset: 0, size: 16 }); // mat4x4
      uniformLayout.set("viewProjection", { offset: 0, size: 16 }); // alias for WGSL
      uniformLayout.set("uTime", { offset: 16, size: 1 }); // f32 after mat4 (byte 64)
      uniformLayout.set("time", { offset: 16, size: 1 }); // alias for WGSL
      // UI rounded overflow-clip (W-UI): vec4s at bytes 80/96, past the aux/time slot. UI shaders
      // pad a float4 into byte 64 so these land 16-aligned; setUniform silently skips them for
      // pipelines whose struct omits them (sprites etc.), so this is inert outside the UI batch.
      uniformLayout.set("uClipRect", { offset: 20, size: 4 }); // (cx, cy, halfX, halfY) physical px
      uniformLayout.set("uClipParams", { offset: 24, size: 4 }); // (radius px, enabled, 0, 0)
      // UI-17c 3D transforms: inverse homography (screen→centered-local) as 3 COLUMNS, bytes 112/128/144.
      // uHom0.w = flag (>0.5 ⇒ perspective quad; fragment recomputes `local` from screenPos). Inert
      // for non-perspective batches (flag 0) and for pipelines whose struct omits the slots.
      uniformLayout.set("uHom0", { offset: 28, size: 4 }); // (a, b, g, flag)
      uniformLayout.set("uHom1", { offset: 32, size: 4 }); // (c, d, h, 0)
      uniformLayout.set("uHom2", { offset: 36, size: 4 }); // (e, f, 1, 0)
      // 'packed' - generic fallback for post-process effects that write entire uniform struct
      uniformLayout.set("packed", { offset: 0, size: 64 }); // up to 256 bytes (64 floats)
    }

    this.pipelines.set(handle, {
      pipeline,
      bindGroupLayout,
      blendMode: 1, // Default to Normal
      usesTextures,
      textureCount,
      uniformLayout,
      hasLighting,
      hasForwardPlus,
    });

    return handle;
  }

  createRenderTarget(
    width: number,
    height: number,
    hasDepth = false,
    hdr = false,
  ): RenderTargetHandle {
    const handle = this.handleCounters.renderTarget++;

    // Use HDR format (rgba16float) or map GPU format to our TextureFormat
    let format: TextureFormat;
    if (hdr) {
      format = "rgba16float";
    } else if (this.presentationFormat === "bgra8unorm") {
      format = "bgra8unorm";
    } else if (this.presentationFormat === "rgba8unorm") {
      format = "rgba8unorm";
    } else {
      log.warn(
        `Unexpected presentation format: ${this.presentationFormat}, falling back to bgra8unorm`,
      );
      format = "bgra8unorm";
    }

    const colorTexture = this.createTexture({
      width,
      height,
      format,
      usage: ["render-target", "sampled", "copy-dst", "copy-src"],
    });

    const colorResource = this.textures.get(colorTexture)!;

    let depthTexture: TextureHandle | undefined;
    let depthView: GPUTextureView | undefined;

    if (hasDepth) {
      depthTexture = this.createTexture({
        width,
        height,
        format: "depth24plus",
        usage: ["render-target"],
      });
      const depthResource = this.textures.get(depthTexture)!;
      depthView = depthResource.gpuView;
    }

    this.renderTargets.set(handle, {
      colorTexture,
      colorView: colorResource.gpuView,
      depthTexture,
      depthView,
      width,
      height,
    });

    return handle;
  }

  // ============================================
  // Resource Destruction
  // ============================================

  destroyTexture(handle: TextureHandle): void {
    const resource = this.textures.get(handle);
    if (resource) {
      // Track VRAM deallocation
      const bytesPerPixel = this.getBytesPerPixel(resource.format);
      this.vramAllocated -= resource.width * resource.height * bytesPerPixel;
      
      resource.gpuTexture.destroy();
      this.textures.delete(handle);
      // A cached bind group may reference this texture's view — drop the cache (handles are
      // monotonic, so this is memory hygiene, not correctness). Fires on render-target resize.
      this.invalidateBindGroupCache();
    }
  }

  destroyBuffer(handle: BufferHandle): void {
    const resource = this.buffers.get(handle);
    if (resource) {
      // Track VRAM deallocation
      this.vramAllocated -= resource.size;

      resource.gpuBuffer.destroy();
      this.buffers.delete(handle);
      this.invalidateBindGroupCache();
    }
  }

  destroyPipeline(handle: PipelineHandle): void {
    this.pipelines.delete(handle);
    this.invalidateBindGroupCache();
  }

  destroyShader(handle: ShaderHandle): void {
    this.shaders.delete(handle);
  }

  destroyRenderTarget(handle: RenderTargetHandle): void {
    const resource = this.renderTargets.get(handle);
    if (resource) {
      this.destroyTexture(resource.colorTexture);
      if (resource.depthTexture) {
        this.destroyTexture(resource.depthTexture);
      }
      this.renderTargets.delete(handle);
    }
  }

  // ============================================
  // MRT (Multiple Render Targets)
  // ============================================

  createMRTRenderTarget(desc: MRTRenderTargetDescriptor): MRTRenderTargetHandle {
    const handle = this.handleCounters.mrtRenderTarget++;

    const colorTextures: TextureHandle[] = [];
    const colorViews: GPUTextureView[] = [];

    for (let i = 0; i < desc.colorAttachments.length; i++) {
      const att = desc.colorAttachments[i];
      const texHandle = this.createTexture({
        width: desc.width,
        height: desc.height,
        format: att.format,
        usage: ['render-target', 'sampled', 'copy-src'],
        label: att.label ?? `${desc.label ?? 'mrt'}-color${i}`,
      });
      const texResource = this.textures.get(texHandle)!;
      colorTextures.push(texHandle);
      colorViews.push(texResource.gpuView);
    }

    let depthTexture: TextureHandle | undefined;
    let depthView: GPUTextureView | undefined;

    if (desc.hasDepth) {
      const depthFormat = desc.depthFormat ?? 'depth24plus';
      depthTexture = this.createTexture({
        width: desc.width,
        height: desc.height,
        format: depthFormat,
        usage: ['render-target', 'sampled'],
        label: `${desc.label ?? 'mrt'}-depth`,
      });
      const depthResource = this.textures.get(depthTexture)!;
      depthView = depthResource.gpuView;
    }

    this.mrtRenderTargets.set(handle, {
      colorTextures,
      colorViews,
      depthTexture,
      depthView,
      width: desc.width,
      height: desc.height,
      label: desc.label,
    });

    return handle;
  }

  destroyMRTRenderTarget(handle: MRTRenderTargetHandle): void {
    const resource = this.mrtRenderTargets.get(handle);
    if (resource) {
      for (const texHandle of resource.colorTextures) {
        this.destroyTexture(texHandle);
      }
      if (resource.depthTexture) {
        this.destroyTexture(resource.depthTexture);
      }
      this.mrtRenderTargets.delete(handle);
    }
  }

  setMRTRenderTarget(handle: MRTRenderTargetHandle): void {
    // End compute pass if active
    this.endComputePass();

    // End current render pass if any
    if (this.renderPassEncoder) {
      this.renderPassEncoder.end();
      this.renderPassEncoder = null;
    }

    // Reset pipeline tracking
    this.currentPipeline = NULL_HANDLE;
    this.currentRenderTarget = NULL_HANDLE;

    // Create command encoder if needed
    if (!this.commandEncoder) {
      this.commandEncoder = this.device.createCommandEncoder({ label: 'ce:mrt-target' });
    }

    const target = this.mrtRenderTargets.get(handle);
    if (!target) {
      log.warn(`setMRTRenderTarget: target not found for handle ${handle}`);
      return;
    }

    this.currentMRTRenderTarget = handle;

    // Build color attachments array — one per MRT attachment
    const colorAttachments: GPURenderPassColorAttachment[] = target.colorViews.map(view => ({
      view,
      loadOp: 'load' as GPULoadOp,
      storeOp: 'store' as GPUStoreOp,
    }));

    const renderPassDescriptor: GPURenderPassDescriptor = {
      colorAttachments,
      depthStencilAttachment: target.depthView
        ? {
            view: target.depthView,
            depthLoadOp: 'load' as GPULoadOp,
            depthStoreOp: 'store' as GPUStoreOp,
          }
        : undefined,
      label: target.label,
    };

    renderPassDescriptor.timestampWrites = this.gpuTimestamps();
    this.renderPassEncoder = this.commandEncoder.beginRenderPass(renderPassDescriptor);
  }

  getMRTRenderTargetInfo(handle: MRTRenderTargetHandle): MRTRenderTargetInfo | null {
    const resource = this.mrtRenderTargets.get(handle);
    if (!resource) return null;
    return {
      width: resource.width,
      height: resource.height,
      colorTextures: [...resource.colorTextures],
      depthTexture: resource.depthTexture ?? NULL_HANDLE,
      attachmentCount: resource.colorTextures.length,
    };
  }

  // ============================================
  // Frame Control
  // ============================================

  beginFrame(): void {
    if (!this.initialized) {
      log.warn(
        "Skipping beginFrame - device not initialized or lost",
      );
      return;
    }

    this.gpuTimer?.beginFrame();
    this._gpuLabel = null;

    this.stats = {
      drawCalls: 0,
      triangles: 0,
      vertices: 0,
      textureBinds: 0,
      pipelineChanges: 0,
      bufferUploads: 0,
      frameTime: performance.now(),
      computeDispatches: 0,
      bindGroupsCreated: 0,
      queueSubmits: 0,
      vramUsed: this.vramAllocated,
      vramAllocated: this.vramTotal || this.vramAllocated, // Total VRAM from adapter or fallback
      textureCount: this.textures.size,
      bufferCount: this.buffers.size,
    };

    this.commandEncoder = this.device.createCommandEncoder({ label: 'ce:frame' });
    this.currentPipeline = NULL_HANDLE;
    this.currentComputePipeline = NULL_HANDLE;
    this.currentBindGroups.clear();
    this.uniformOffset = 0;
    this.uniformDirtyBytes = 0;
    this.uniformPoolIndex = 0;
    this.capturedUniformBuffer = null;
  }

  endFrame(): void {
    // End compute pass if active
    this.endComputePass();

    if (this.renderPassEncoder) {
      this.renderPassEncoder.end();
      this.renderPassEncoder = null;
    }

    // Resolve GPU timestamp queries into a readback buffer before submitting.
    // The query set retains values across the frame's submits, so one resolve at
    // end-of-frame is correct even when flushCompute split the frame.
    if (this.gpuTimer?.hasPending()) {
      if (!this.commandEncoder) this.commandEncoder = this.device.createCommandEncoder({ label: 'ce:gpu-timer-resolve' });
      this.gpuTimer.resolve(this.commandEncoder);
    }

    if (this.commandEncoder) {
      const commandBuffer = this.commandEncoder.finish();
      this.stats.queueSubmits++;
      this.device.queue.submit([commandBuffer]);
      this.commandEncoder = null;
    }

    // Kick the async map now that the resolve is submitted.
    this.gpuTimer?.readback();

    this.stats.frameTime = performance.now() - this.stats.frameTime;
  }

  getStats(): RenderStats {
    return {
      ...this.stats,
      gpuTime: this.gpuTimer?.lastGpuTimeMs ?? 0,
      gpuPasses: this.gpuTimer?.lastPasses ?? [],
    };
  }

  // ============================================
  // GPU timing markers
  // ============================================

  /** Label GPU passes recorded until the matching {@link popGpuMarker}. */
  pushGpuMarker(label: string): void {
    this._gpuLabel = label;
  }

  popGpuMarker(): void {
    this._gpuLabel = null;
  }

  /** Timestamp pair for the next pass, attributed to the current marker. */
  private gpuTimestamps(): { querySet: GPUQuerySet; beginningOfPassWriteIndex: number; endOfPassWriteIndex: number } | undefined {
    return this.gpuTimer?.allocPair(this._gpuLabel ?? 'Unlabeled');
  }

  // Variant A: the native GPUDevice exposes createBindGroupPacked, which reads all entry scalars in a
  // single typed-array marshal instead of ~14 per-field ClearScript GetProperty hops (the hop-per-field
  // marshaling was driving the native createBindGroup GC spikes). Feature-detected once; the browser
  // has no such method so _packedBindGroups stays false and the standard descriptor path is used.
  private _packedBindGroups: boolean | undefined = undefined;
  private _bgScalars = new Float64Array(48);   // [binding, offset, size] * up to 16 entries; grows on demand
  private _bgResources: unknown[] = [];          // parallel resource host-objects; reused across calls

  /**
   * Single funnel for GPUBindGroup creation so every allocation is counted in
   * stats.bindGroupsCreated. The bind-group cache (createAndBindTextureGroup et al.)
   * routes its cache-misses through here, so the counter measures real churn.
   */
  private _makeBindGroup(descriptor: GPUBindGroupDescriptor): GPUBindGroup {
    this.stats.bindGroupsCreated++;

    const dev = this.device as unknown as {
      createBindGroupPacked?: (layout: unknown, scalars: Float64Array, resources: unknown[], count: number) => GPUBindGroup;
    };
    if (this._packedBindGroups === undefined) {
      this._packedBindGroups = typeof dev.createBindGroupPacked === 'function';
      // One-time signal so we can tell from the console whether the native fast path is actually live
      // (e.g. player_backend rebuilt but player.js wasn't → this stays off and createBindGroup is unchanged).
      console.log(`[render2d] packed bind groups: ${this._packedBindGroups ? 'ON (native createBindGroupPacked)' : 'off (standard descriptor)'}`);
    }

    if (this._packedBindGroups) {
      try {
        const entries = descriptor.entries as unknown as GPUBindGroupEntry[];
        const n = entries.length;
        let scalars = this._bgScalars;
        if (scalars.length < n * 3) scalars = this._bgScalars = new Float64Array(n * 3);
        const res = this._bgResources;
        res.length = n;
        for (let i = 0; i < n; i++) {
          const r = entries[i].resource as { buffer?: unknown; offset?: number; size?: number };
          const base = i * 3;
          scalars[base] = entries[i].binding;
          // Buffer binding is a plain { buffer, offset?, size? }; samplers/views are opaque host
          // objects (their `.buffer` is undefined) and get type-checked on the C# side. size 0 ⇒ whole buffer.
          if (r && r.buffer !== undefined) {
            res[i] = r.buffer;
            scalars[base + 1] = r.offset ?? 0;
            scalars[base + 2] = r.size ?? 0;
          } else {
            res[i] = r;
            scalars[base + 1] = 0;
            scalars[base + 2] = 0;
          }
        }
        return dev.createBindGroupPacked!(descriptor.layout, scalars, res, n);
      } catch (e) {
        // A JS/marshaling-level failure self-disables the packed path for the rest of the session and
        // falls back to the standard descriptor. (The packing mirrors the standard parse field-for-field,
        // so a native validation panic shouldn't be reachable — but if it were, it can't be caught here.)
        this._packedBindGroups = false;
        log.warn('createBindGroupPacked failed, falling back to createBindGroup', e);
      }
    }

    return this.device.createBindGroup(descriptor);
  }

  /** Stable per-context identity id for a GPUBuffer, used in bind-group cache keys. */
  private bufferId(buf: GPUBuffer): number {
    let id = this.bindGroupBufferIds.get(buf);
    if (id === undefined) {
      id = this._bindGroupBufferIdNext++;
      this.bindGroupBufferIds.set(buf, id);
    }
    return id;
  }

  /** Insert into the bind-group cache, clearing it wholesale if it grew past its cap. */
  private cacheBindGroup(key: string, group: GPUBindGroup): void {
    if (this.bindGroupCache.size >= WebGPURenderContext.BIND_GROUP_CACHE_CAP) {
      this.bindGroupCache.clear();
    }
    this.bindGroupCache.set(key, group);
  }

  /** Drop all cached bind groups and force group-1 rebuilds — called when a referenced resource dies. */
  private invalidateBindGroupCache(): void {
    if (this.bindGroupCache.size > 0) this.bindGroupCache.clear();
    this._lightingBindGroupKey = '';
    this._forwardPlusBindGroupKey = '';
  }

  /**
   * Handle canvas resize - reconfigure the WebGPU context
   * Must be called after changing canvas.width/height
   */
  handleResize(): void {
    if (!this.initialized) return;

    // Reconfigure the context with the new canvas size. Reuse the alpha mode chosen at
    // init — hardcoding "opaque" here silently dropped premultiplied alpha after resize.
    this.context.configure({
      device: this.device,
      format: this.presentationFormat,
      alphaMode: this.configuredAlphaMode,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_DST | GPUTextureUsage.COPY_SRC,
    });
  }

  // ============================================
  // Render Target
  // ============================================

  setRenderTarget(handle: RenderTargetHandle | null): void {
    // End compute pass if active (must happen before render pass)
    this.endComputePass();

    // End current render pass if any
    if (this.renderPassEncoder) {
      this.renderPassEncoder.end();
      this.renderPassEncoder = null;
    }

    // Reset pipeline tracking - new render pass needs setPipeline to be called
    this.currentPipeline = NULL_HANDLE;
    this.currentMRTRenderTarget = NULL_HANDLE;

    // Create command encoder if needed (may be null after flushCompute)
    if (!this.commandEncoder) {
      this.commandEncoder = this.device.createCommandEncoder({ label: 'ce:render-target' });
    }

    let colorView: GPUTextureView;
    let depthView: GPUTextureView | undefined;
    let passLabel: string;

    if (handle === null || handle === NULL_HANDLE) {
      // Render to canvas
      colorView = this.context.getCurrentTexture().createView();
      this.currentRenderTarget = NULL_HANDLE;
      passLabel = 'pass:backbuffer(load)';
    } else {
      const target = this.renderTargets.get(handle);
      if (!target) return;

      colorView = target.colorView;
      depthView = target.depthView;
      this.currentRenderTarget = handle;
      passLabel = `pass:rt#${handle}(load)`;
    }

    const renderPassDescriptor: GPURenderPassDescriptor = {
      colorAttachments: [
        {
          view: colorView,
          loadOp: "load",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: depthView
        ? {
            view: depthView,
            depthLoadOp: "load",
            depthStoreOp: "store",
          }
        : undefined,
      label: passLabel,
    };

    renderPassDescriptor.timestampWrites = this.gpuTimestamps();
    this.renderPassEncoder =
      this.commandEncoder.beginRenderPass(renderPassDescriptor);
  }

  getRenderTargetTexture(handle: RenderTargetHandle): TextureHandle {
    const target = this.renderTargets.get(handle);
    return target?.colorTexture ?? NULL_HANDLE;
  }

  copyBackbufferToTexture(dst: TextureHandle, width: number, height: number): boolean {
    if (!this.initialized) return false;
    // Only the swapchain path is supported — the one consumer (UI backdrop blur) runs on the
    // backbuffer after post-process. Offscreen current-target copies can join when needed.
    if (this.currentRenderTarget !== NULL_HANDLE) return false;
    const tex = this.textures.get(dst);
    if (!tex) return false;

    // copyTextureToTexture must be recorded OUTSIDE a render pass; passes restart with
    // loadOp:'load' (see setRenderTarget), so ending mid-frame is content-safe.
    this.endComputePass();
    if (this.renderPassEncoder) {
      this.renderPassEncoder.end();
      this.renderPassEncoder = null;
    }
    this.currentPipeline = NULL_HANDLE;
    if (!this.commandEncoder) {
      this.commandEncoder = this.device.createCommandEncoder({ label: 'ce:backbuffer-copy' });
    }

    const src = this.context.getCurrentTexture();
    try {
      this.commandEncoder.copyTextureToTexture(
        { texture: src },
        { texture: tex.gpuTexture },
        {
          width: Math.min(width, src.width, tex.width),
          height: Math.min(height, src.height, tex.height),
          depthOrArrayLayers: 1,
        },
      );
    } catch {
      // Host without swapchain COPY_SRC (native shim gap) — caller degrades gracefully.
      return false;
    }
    return true;
  }

  // ============================================
  // State Setting
  // ============================================

  setPipeline(handle: PipelineHandle): void {
    if (this.currentPipeline === handle) return;
    if (!this.renderPassEncoder) {
      log.warn(
        `setPipeline: no renderPassEncoder for handle ${handle}`,
      );
      return;
    }

    const pipeline = this.pipelines.get(handle);
    if (!pipeline) {
      log.warn(
        `setPipeline: pipeline not found for handle ${handle}, available: ${[...this.pipelines.keys()].join(",")}`,
      );
      return;
    }

    this.renderPassEncoder.setPipeline(pipeline.pipeline);
    this.currentPipeline = handle;
    this.boundTextures.clear(); // Reset bound textures on pipeline change
    this.stats.pipelineChanges++;
  }

  bindTexture(slot: number, handle: TextureHandle): void {
    if (!this.renderPassEncoder) return;

    const resource = this.textures.get(handle);
    if (!resource) return;

    // Store bound texture by slot
    this.boundTextures.set(slot, handle);

    const pipeline = this.pipelines.get(this.currentPipeline);
    if (!pipeline) return;

    // Create bind group when we have all required textures
    if (this.boundTextures.size >= pipeline.textureCount) {
      this.createAndBindTextureGroup(pipeline);
    }

    this.stats.textureBinds++;
  }

  private createAndBindTextureGroup(pipeline: PipelineResource): void {
    if (!this.renderPassEncoder) return;

    // Get first texture for sampler (all textures share same sampler)
    const firstHandle = this.boundTextures.get(0);
    if (!firstHandle) return;
    const firstResource = this.textures.get(firstHandle);
    if (!firstResource) return;

    // Resolve the group-0 uniform buffer FIRST — this consumes the per-draw snapshot and records
    // sharedUniformBound, side effects that must run whether or not the bind group is already cached.
    const uniformBuf = this.resolveDrawUniformBuffer();

    // Cache key: pipeline handle (implies its bind-group layout), the uniform buffer identity, and the
    // bound texture handles (handle 0 also fixes the sampler). All monotonic ⇒ a key can't false-hit.
    let key = `t${this.currentPipeline}|${this.bufferId(uniformBuf)}`;
    for (let i = 0; i < pipeline.textureCount; i++) {
      const texHandle = this.boundTextures.get(i);
      if (!texHandle) {
        log.warn(`Missing texture at slot ${i}`);
        return;
      }
      key += `|${texHandle}`;
    }

    let bindGroup = this.bindGroupCache.get(key);
    if (bindGroup === undefined) {
      const entries: GPUBindGroupEntry[] = [
        { binding: 0, resource: { buffer: uniformBuf } },
        { binding: 1, resource: firstResource.sampler },
      ];
      for (let i = 0; i < pipeline.textureCount; i++) {
        const texHandle = this.boundTextures.get(i)!;
        const texResource = this.textures.get(texHandle);
        if (!texResource) {
          log.warn(`Texture resource not found for handle ${texHandle}`);
          return;
        }
        entries.push({ binding: 2 + i, resource: texResource.gpuView });
      }
      bindGroup = this._makeBindGroup({ layout: pipeline.bindGroupLayout, entries });
      this.cacheBindGroup(key, bindGroup);
    }

    this.renderPassEncoder.setBindGroup(0, bindGroup);

    // Bind lighting data at group(1): Forward+ takes precedence over basic forward-lit
    if (pipeline.hasForwardPlus) {
      // hasForwardPlus pipelines REQUIRE group(1); fall back to the empty safety-net group when no real
      // light data is bound (no subsystem / cull not ready) so the draw degrades instead of crashing.
      this.renderPassEncoder.setBindGroup(1, this.forwardPlusBindGroup ?? this.getEmptyForwardPlusBindGroup());
    } else if (pipeline.hasLighting && this.lightingBindGroup) {
      this.renderPassEncoder.setBindGroup(1, this.lightingBindGroup);
    }
  }

  /**
   * Return the cached group-0 uniform-only bind group for the current (non-textured) pipeline, building
   * it on a miss. Calls resolveDrawUniformBuffer() exactly once (consuming any per-draw snapshot) so the
   * snapshot/shared bookkeeping is identical to the pre-cache path.
   */
  private getUniformOnlyGroup(pipeline: PipelineResource): GPUBindGroup {
    const uniformBuf = this.resolveDrawUniformBuffer();
    const key = `u${this.currentPipeline}|${this.bufferId(uniformBuf)}`;
    let g = this.bindGroupCache.get(key);
    if (g === undefined) {
      g = this._makeBindGroup({
        layout: pipeline.bindGroupLayout,
        entries: [{ binding: 0, resource: { buffer: uniformBuf } }],
      });
      this.cacheBindGroup(key, g);
    }
    return g;
  }

  bindUniforms(): void {
    if (!this.renderPassEncoder) return;

    const pipeline = this.pipelines.get(this.currentPipeline);
    if (!pipeline) return;

    // If pipeline uses textures, we need to include them in bind group
    if (pipeline.usesTextures) {
      // Use bound textures or skip if none bound (bind group was already set by bindTexture)
      if (this.boundTextures.size > 0) {
        // Already bound via bindTexture, no need to rebind
        return;
      }
      // No texture bound but pipeline expects one - this is an error
      log.warn(
        "bindUniforms called on texture pipeline without texture bound",
      );
      return;
    }

    // For pipelines without textures, bind just the uniform buffer (cached).
    const bindGroup = this.getUniformOnlyGroup(pipeline);
    this.renderPassEncoder.setBindGroup(0, bindGroup);

    // Bind lighting data at group(1): Forward+ takes precedence over basic forward-lit
    if (pipeline.hasForwardPlus) {
      // hasForwardPlus pipelines REQUIRE group(1); fall back to the empty safety-net group when no real
      // light data is bound (no subsystem / cull not ready) so the draw degrades instead of crashing.
      this.renderPassEncoder.setBindGroup(1, this.forwardPlusBindGroup ?? this.getEmptyForwardPlusBindGroup());
    } else if (pipeline.hasLighting && this.lightingBindGroup) {
      this.renderPassEncoder.setBindGroup(1, this.lightingBindGroup);
    }
  }

  // ============================================
  // Forward-Lit Lighting Bind Group (group 1)
  // ============================================

  /**
   * Lazy-create the shared bind group layout for lighting data (group 1).
   * Layout: binding(0) = LightUniforms (uniform), binding(1) = lights[] (storage).
   */
  private getLightingBindGroupLayout(): GPUBindGroupLayout {
    if (!this.lightingBindGroupLayout) {
      this.lightingBindGroupLayout = this.device.createBindGroupLayout({
        label: 'lighting-bgl',
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
            buffer: { type: 'uniform' },
          },
          {
            binding: 1,
            visibility: GPUShaderStage.FRAGMENT,
            buffer: { type: 'read-only-storage' },
          },
        ],
      });
    }
    return this.lightingBindGroupLayout;
  }

  /**
   * Bind lighting data for forward-lit rendering (group 1).
   * Creates a bind group from the given uniform + storage buffer handles.
   */
  setLightingData(uniformBuffer: BufferHandle, storageBuffer: BufferHandle): void {
    const uniformBuf = this.buffers.get(uniformBuffer);
    const storageBuf = this.buffers.get(storageBuffer);
    if (!uniformBuf || !storageBuf) {
      log.warn('setLightingData: buffer(s) not found');
      return;
    }
    // The buffers keep their identity frame-to-frame (LightManager reuses them and re-uploads
    // contents), so rebuild the group only when the input buffers actually change.
    const key = `${uniformBuffer}|${storageBuffer}`;
    if (this._lightingBindGroupKey === key && this.lightingBindGroup) return;
    this._lightingBindGroupKey = key;
    this.lightingBindGroup = this._makeBindGroup({
      layout: this.getLightingBindGroupLayout(),
      entries: [
        { binding: 0, resource: { buffer: uniformBuf.gpuBuffer } },
        { binding: 1, resource: { buffer: storageBuf.gpuBuffer } },
      ],
      label: 'forward-lighting-bg',
    });
  }

  /** Clear the lighting bind group. */
  clearLightingData(): void {
    this.lightingBindGroup = null;
    this._lightingBindGroupKey = '';
  }

  // ============================================
  // Forward+ Lighting Bind Group (group 1, with tile data)
  // ============================================

  /**
   * Lazy-create the bind group layout for Forward+ lighting data (group 1).
   * Layout:
   *   binding(0) = LightUniforms (uniform)
   *   binding(1) = lights[] (read-only storage)
   *   binding(2) = tileLightData[] (read-only storage)
   *   binding(3) = shadowMapData[] (read-only storage)
   */
  private getForwardPlusBindGroupLayout(): GPUBindGroupLayout {
    if (!this.forwardPlusBindGroupLayout) {
      this.forwardPlusBindGroupLayout = this.device.createBindGroupLayout({
        label: 'forward-plus-bgl',
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
            buffer: { type: 'uniform' },
          },
          {
            binding: 1,
            visibility: GPUShaderStage.FRAGMENT,
            buffer: { type: 'read-only-storage' },
          },
          {
            binding: 2,
            visibility: GPUShaderStage.FRAGMENT,
            buffer: { type: 'read-only-storage' },
          },
          {
            binding: 3,
            visibility: GPUShaderStage.FRAGMENT,
            buffer: { type: 'read-only-storage' },
          },
          {
            binding: 4,
            visibility: GPUShaderStage.FRAGMENT,
            buffer: { type: 'read-only-storage' },
          },
        ],
      });
    }
    return this.forwardPlusBindGroupLayout;
  }

  /** Get or create a tiny dummy buffer for shadow map binding when no shadows exist. */
  private getDummyShadowBuffer(): GPUBuffer {
    if (!this._dummyShadowBuffer) {
      this._dummyShadowBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.STORAGE,
        label: 'dummy-shadow-buffer',
      });
    }
    return this._dummyShadowBuffer;
  }

  /** Get or create a tiny dummy buffer for IES profile binding when no profiles exist. */
  private getDummyIESBuffer(): GPUBuffer {
    if (!this._dummyIESBuffer) {
      this._dummyIESBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.STORAGE,
        label: 'dummy-ies-buffer',
      });
    }
    return this._dummyIESBuffer;
  }

  /**
   * Lazily build the empty Forward+ bind group — the safety net for a lit (hasForwardPlus) material drawn
   * when no real light data is bound (no lighting subsystem, cull not ready, OR the material preview).
   * Seeds a valid LightUniforms: **white ambient** (so `applyLighting2D` → `albedo * white = albedo`, i.e.
   * the material shows unlit-bright instead of black) and **tileSize=16 / maxLightsPerTile=0** so the tile
   * math has no divide-by-zero (which drivers turn into a black fragment) and the light loop is skipped.
   */
  private getEmptyForwardPlusBindGroup(): GPUBindGroup {
    if (this._emptyForwardPlusBindGroup) return this._emptyForwardPlusBindGroup;

    // Seed a valid LightUniforms via the struct writer (no hand-rolled byte offsets): white ambient (lit
    // material → albedo), tileSize=16 (avoids the divide-by-zero that drivers turn into a black fragment),
    // maxLightsPerTile=0 (the tile light loop is skipped). Other fields stay zero (unused with 0 lights).
    const lu = LIGHT_UNIFORMS.createWriter();
    lu.setAll({
      ambientColor: [1, 1, 1, 1],
      screenSize: [1, 1],
      fpConfig: [16, 0, 1, 1], // tileSize, maxLightsPerTile, shadowMapRes, iesResolution
    });
    this._dummyLightUniformBuffer = this.device.createBuffer({
      size: LIGHT_UNIFORMS.size,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      label: 'empty-light-uniform',
    });
    this.device.queue.writeBuffer(this._dummyLightUniformBuffer, 0, lu.buffer);

    // One zeroed storage buffer shared by the lights + tile-light bindings (0 lights → never indexed in-bounds).
    this._dummyLightStorageBuffer = this.device.createBuffer({
      size: 256,
      usage: GPUBufferUsage.STORAGE,
      label: 'empty-light-storage',
    });

    this._emptyForwardPlusBindGroup = this._makeBindGroup({
      layout: this.getForwardPlusBindGroupLayout(),
      entries: [
        { binding: 0, resource: { buffer: this._dummyLightUniformBuffer } },
        { binding: 1, resource: { buffer: this._dummyLightStorageBuffer } },
        { binding: 2, resource: { buffer: this._dummyLightStorageBuffer } },
        { binding: 3, resource: { buffer: this.getDummyShadowBuffer() } },
        { binding: 4, resource: { buffer: this.getDummyIESBuffer() } },
      ],
      label: 'empty-forward-plus-bg',
    });
    return this._emptyForwardPlusBindGroup;
  }

  /**
   * Bind Forward+ lighting data (group 1) with tile light index buffer, shadow map and IES profiles.
   */
  setForwardPlusData(uniformBuffer: BufferHandle, storageBuffer: BufferHandle, tileLightBuffer: BufferHandle, shadowMapBuffer?: BufferHandle, iesProfileBuffer?: BufferHandle): void {
    const uniformBuf = this.buffers.get(uniformBuffer);
    const storageBuf = this.buffers.get(storageBuffer);
    const tileBuf = this.buffers.get(tileLightBuffer);
    if (!uniformBuf || !storageBuf || !tileBuf) {
      log.warn('setForwardPlusData: buffer(s) not found');
      return;
    }

    // Resolve shadow map buffer — use provided or a tiny dummy
    let shadowBuf: GPUBuffer;
    if (shadowMapBuffer != null) {
      const resolved = this.buffers.get(shadowMapBuffer);
      shadowBuf = resolved ? resolved.gpuBuffer : this.getDummyShadowBuffer();
    } else {
      shadowBuf = this.getDummyShadowBuffer();
    }

    // Resolve IES profile buffer — use provided or a tiny dummy
    let iesBuf: GPUBuffer;
    if (iesProfileBuffer != null) {
      const resolved = this.buffers.get(iesProfileBuffer);
      iesBuf = resolved ? resolved.gpuBuffer : this.getDummyIESBuffer();
    } else {
      iesBuf = this.getDummyIESBuffer();
    }

    // Rebuild only when an input buffer changes; contents are re-uploaded into stable buffers each frame.
    const key = `${uniformBuffer}|${storageBuffer}|${tileLightBuffer}|${shadowMapBuffer ?? -1}|${iesProfileBuffer ?? -1}`;
    if (this._forwardPlusBindGroupKey === key && this.forwardPlusBindGroup) return;
    this._forwardPlusBindGroupKey = key;

    this.forwardPlusBindGroup = this._makeBindGroup({
      layout: this.getForwardPlusBindGroupLayout(),
      entries: [
        { binding: 0, resource: { buffer: uniformBuf.gpuBuffer } },
        { binding: 1, resource: { buffer: storageBuf.gpuBuffer } },
        { binding: 2, resource: { buffer: tileBuf.gpuBuffer } },
        { binding: 3, resource: { buffer: shadowBuf } },
        { binding: 4, resource: { buffer: iesBuf } },
      ],
      label: 'forward-plus-bg',
    });
  }

  /** Clear the Forward+ bind group. */
  clearForwardPlusData(): void {
    this.forwardPlusBindGroup = null;
    this._forwardPlusBindGroupKey = '';
  }

  bindVertexBuffer(handle: BufferHandle): void {
    if (!this.renderPassEncoder) return;

    const resource = this.buffers.get(handle);
    if (!resource) {
      log.warn(`bindVertexBuffer: buffer ${handle} not found`);
      return;
    }

    this.renderPassEncoder.setVertexBuffer(0, resource.gpuBuffer);
  }

  bindIndexBuffer(handle: BufferHandle): void {
    if (!this.renderPassEncoder) return;

    const resource = this.buffers.get(handle);
    if (!resource) return;

    this.renderPassEncoder.setIndexBuffer(resource.gpuBuffer, "uint16");
  }

  captureUniforms(): void {
    if (this.uniformOffset === 0) return;

    // Grow pool on demand
    if (this.uniformPoolIndex >= this.uniformBufferPool.length) {
      const buf = this.device.createBuffer({
        size: 4096, // Same size as shared uniformBuffer
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        label: `uniform-snapshot-${this.uniformPoolIndex}`,
      });
      this.uniformBufferPool.push(buf);
    }

    const buf = this.uniformBufferPool[this.uniformPoolIndex++];
    const byteSize = this.uniformOffset * 4;
    this.device.queue.writeBuffer(buf, 0, this.uniformData.buffer, 0, byteSize);
    this.capturedUniformBuffer = buf;
  }

  setUniform(name: string, value: number | Float32Array | Int32Array): void {
    // Get current pipeline's uniform layout
    const pipeline = this.pipelines.get(this.currentPipeline);
    const layout = pipeline?.uniformLayout.get(name);

    // Skip if uniform not found in current pipeline layout
    // This prevents corruption from WebGL-style texture unit assignments
    if (!layout) {
      // Silently skip - this is common for WebGL patterns like setUniform('uTexture', 0)
      // which don't apply to WebGPU where textures are in bind groups
      return;
    }

    const writeOffset = layout.offset;

    // Write to uniform data buffer
    if (typeof value === "number") {
      this.uniformData[writeOffset] = value;
      this.uniformOffset = Math.max(this.uniformOffset, writeOffset + 1);
    } else {
      for (let i = 0; i < value.length; i++) {
        this.uniformData[writeOffset + i] = value[i];
      }
      this.uniformOffset = Math.max(this.uniformOffset, writeOffset + value.length);
    }

    // Mark the shared uniform buffer dirty instead of uploading here. The snapshot path
    // (captureUniforms) re-uploads from uniformData into a pool buffer anyway, so the old
    // per-setUniform writeBuffer was pure redundant traffic (a big share of the per-frame
    // writeBuffer count). getSharedUniformBuffer() does the single deferred upload if/when a
    // non-snapshot consumer actually binds `uniformBuffer`.
    // For 'packed' arrays (post-process effects), account for the full array length so all
    // struct fields (including trailing alignment) are covered by the eventual upload.
    if (this.uniformBuffer) {
      const byteSize = (typeof value !== "number" && name === "packed")
        ? value.length * 4  // full packed struct
        : this.uniformOffset * 4;  // up to last written offset
      if (byteSize > this.uniformDirtyBytes) this.uniformDirtyBytes = byteSize;
    }
  }

  /**
   * Returns the shared uniform buffer, first flushing any pending setUniform writes as a single
   * upload. Callers that bind `this.uniformBuffer` directly (non-snapshot paths: unlit-without-
   * capture, post-process, compute fallback) MUST resolve it through here so its contents are
   * current. The snapshot path never calls this — it reads uniformData straight into a pool buffer.
   */
  private getSharedUniformBuffer(): GPUBuffer {
    if (this.uniformDirtyBytes > 0 && this.uniformBuffer) {
      this.device.queue.writeBuffer(
        this.uniformBuffer,
        0,
        this.uniformData.buffer,
        0,
        this.uniformDirtyBytes,
      );
      this.uniformDirtyBytes = 0;
    }
    return this.uniformBuffer!;
  }

  /**
   * Resolve the group-0 uniform buffer for a draw and record which backing it uses. Returns the
   * per-draw snapshot when one was captured (sprite path — isolated, zero shared writes), else the
   * shared buffer. Sets `sharedUniformBound` so a following draw() can flush setUniform writes issued
   * AFTER the bind (post-process sets 'packed' uniforms between bindTexture and draw); the snapshot
   * path leaves it false and never pays a per-draw shared write.
   */
  private resolveDrawUniformBuffer(): GPUBuffer {
    if (this.capturedUniformBuffer) {
      const snapshot = this.capturedUniformBuffer;
      this.capturedUniformBuffer = null;
      this.sharedUniformBound = false;
      return snapshot;
    }
    this.sharedUniformBound = true;
    return this.getSharedUniformBuffer();
  }

  /** Flush setUniform writes made AFTER the shared buffer was bound, just before the draw submits. */
  private flushSharedUniformsBeforeDraw(): void {
    if (this.sharedUniformBound && this.uniformDirtyBytes > 0) this.getSharedUniformBuffer();
  }

  setUniformInt(name: string, value: number): void {
    this.setUniform(name, value);
  }

  setupVertexAttributes(_layout: VertexBufferLayout): void {
    // In WebGPU, vertex attributes are defined in the pipeline
    // This is a no-op
  }

  // ============================================
  // Viewport / Scissor
  // ============================================

  setViewport(x: number, y: number, width: number, height: number): void {
    if (!this.renderPassEncoder) return;
    this.renderPassEncoder.setViewport(x, y, width, height, 0, 1);
  }

  setScissor(x: number, y: number, width: number, height: number): void {
    if (!this.renderPassEncoder) return;
    this.renderPassEncoder.setScissorRect(x, y, width, height);
  }

  disableScissor(): void {
    // Reset scissor to the CURRENT attachment's full size — not the canvas: when an offscreen
    // render target is bound (post-process chain, UI backdrop blur levels) a canvas-sized rect
    // overflows the attachment and invalidates the whole command buffer (Dawn validation).
    if (!this.renderPassEncoder) return;
    const rt =
      this.currentRenderTarget !== NULL_HANDLE ? this.renderTargets.get(this.currentRenderTarget) : undefined;
    this.renderPassEncoder.setScissorRect(
      0,
      0,
      rt?.width ?? this.canvas.width,
      rt?.height ?? this.canvas.height,
    );
  }

  // ============================================
  // Blend Mode
  // ============================================

  /**
   * @deprecated No-op. Blend state lives in the pipeline, not in mutable context state. Blend is
   * now baked into pipelines by their owners — Material bakes renderState.blendMode into its color
   * targets, and SpriteBatch.setBlendMode selects a pre-built blend pipeline variant. Kept only to
   * satisfy the IRenderContext interface (native player + test mocks).
   */
  setBlendMode(_mode: BlendMode): void {
    // Intentionally empty — see the deprecation note above.
  }

  // ============================================
  // Drawing
  // ============================================

  clear(r: number, g: number, b: number, a = 1): void {
    if (!this.initialized) return;

    // End current render pass to start a new one with clear
    if (this.renderPassEncoder) {
      this.renderPassEncoder.end();
      this.renderPassEncoder = null;
    }

    // Reset pipeline tracking - new render pass needs setPipeline to be called
    this.currentPipeline = NULL_HANDLE;

    // Create command encoder if needed (may be null after flushCompute)
    if (!this.commandEncoder) {
      this.commandEncoder = this.device.createCommandEncoder({ label: 'ce:clear' });
    }

    // ---- MRT clear path ----
    if (this.currentMRTRenderTarget !== NULL_HANDLE) {
      const target = this.mrtRenderTargets.get(this.currentMRTRenderTarget);
      if (!target) return;

      const colorAttachments: GPURenderPassColorAttachment[] = target.colorViews.map(view => ({
        view,
        clearValue: { r, g, b, a },
        loadOp: 'clear' as GPULoadOp,
        storeOp: 'store' as GPUStoreOp,
      }));

      this.renderPassEncoder = this.commandEncoder.beginRenderPass({
        colorAttachments,
        depthStencilAttachment: target.depthView
          ? {
              view: target.depthView,
              depthLoadOp: 'clear' as GPULoadOp,
              depthClearValue: 1.0,
              depthStoreOp: 'store' as GPUStoreOp,
            }
          : undefined,
        label: target.label ? `${target.label}-clear` : undefined,
        timestampWrites: this.gpuTimestamps(),
      });
      return;
    }

    // ---- Single render target / backbuffer clear path ----
    let colorView: GPUTextureView;

    try {
      if (this.currentRenderTarget === NULL_HANDLE) {
        colorView = this.context.getCurrentTexture().createView();
      } else {
        const target = this.renderTargets.get(this.currentRenderTarget);
        if (!target) return;
        colorView = target.colorView;
      }
    } catch (e) {
      log.fatal("Failed to get current texture:", e);
      return;
    }

    const renderPassDescriptor: GPURenderPassDescriptor = {
      colorAttachments: [
        {
          view: colorView,
          clearValue: { r, g, b, a },
          loadOp: "clear",
          storeOp: "store",
        },
      ],
      label: this.currentRenderTarget === NULL_HANDLE
        ? 'pass:backbuffer(clear)'
        : `pass:rt#${this.currentRenderTarget}(clear)`,
    };

    renderPassDescriptor.timestampWrites = this.gpuTimestamps();
    this.renderPassEncoder =
      this.commandEncoder.beginRenderPass(renderPassDescriptor);
  }

  draw(vertexCount: number, first = 0): void {
    if (!this.renderPassEncoder) return;

    // For non-textured pipelines, we need to set uniform-only bind group
    const pipeline = this.pipelines.get(this.currentPipeline);
    if (pipeline && !pipeline.usesTextures && this.uniformBuffer) {
      const bindGroup = this.getUniformOnlyGroup(pipeline);
      this.renderPassEncoder.setBindGroup(0, bindGroup);

      // Bind lighting data at group(1): Forward+ takes precedence over basic forward-lit
      if (pipeline.hasForwardPlus && this.forwardPlusBindGroup) {
        this.renderPassEncoder.setBindGroup(1, this.forwardPlusBindGroup);
      } else if (pipeline.hasLighting && this.lightingBindGroup) {
        this.renderPassEncoder.setBindGroup(1, this.lightingBindGroup);
      }
    }

    this.flushSharedUniformsBeforeDraw();
    this.renderPassEncoder.draw(vertexCount, 1, first, 0);
    this.stats.drawCalls++;
    this.stats.vertices += vertexCount;
    this.stats.triangles += vertexCount / 3;
  }

  drawIndexed(indexCount: number, first = 0): void {
    if (!this.renderPassEncoder) return;

    // For non-textured pipelines, set the uniform-only bind group (same as draw/drawInstanced —
    // this path was never exercised before UIBatch: indexed draws had always been textured).
    const pipeline = this.pipelines.get(this.currentPipeline);
    if (pipeline && !pipeline.usesTextures && this.uniformBuffer) {
      const bindGroup = this.getUniformOnlyGroup(pipeline);
      this.renderPassEncoder.setBindGroup(0, bindGroup);

      if (pipeline.hasForwardPlus && this.forwardPlusBindGroup) {
        this.renderPassEncoder.setBindGroup(1, this.forwardPlusBindGroup);
      } else if (pipeline.hasLighting && this.lightingBindGroup) {
        this.renderPassEncoder.setBindGroup(1, this.lightingBindGroup);
      }
    }

    this.flushSharedUniformsBeforeDraw();
    this.renderPassEncoder.drawIndexed(indexCount, 1, first, 0, 0);
    this.stats.drawCalls++;
    this.stats.vertices += indexCount;
    this.stats.triangles += indexCount / 3;
  }

  drawInstanced(
    vertexCount: number,
    instanceCount: number,
    firstVertex = 0,
    firstInstance = 0,
  ): void {
    if (!this.renderPassEncoder) {
      log.warn("drawInstanced: no renderPassEncoder");
      return;
    }

    // For non-textured pipelines, we need to set uniform-only bind group
    const pipeline = this.pipelines.get(this.currentPipeline);
    if (!pipeline) {
      log.warn(
        `drawInstanced: pipeline not found for handle ${this.currentPipeline}`,
      );
    }
    if (pipeline && !pipeline.usesTextures && this.uniformBuffer) {
      const bindGroup = this.getUniformOnlyGroup(pipeline);
      this.renderPassEncoder.setBindGroup(0, bindGroup);

      // Bind lighting data at group(1): Forward+ takes precedence over basic forward-lit
      if (pipeline.hasForwardPlus && this.forwardPlusBindGroup) {
        this.renderPassEncoder.setBindGroup(1, this.forwardPlusBindGroup);
      } else if (pipeline.hasLighting && this.lightingBindGroup) {
        this.renderPassEncoder.setBindGroup(1, this.lightingBindGroup);
      }
    }

    this.flushSharedUniformsBeforeDraw();
    this.renderPassEncoder.draw(
      vertexCount,
      instanceCount,
      firstVertex,
      firstInstance,
    );
    this.stats.drawCalls++;
    this.stats.vertices += vertexCount * instanceCount;
    this.stats.triangles += (vertexCount / 3) * instanceCount;
  }

  // ============================================
  // Resource Info
  // ============================================

  getTextureSize(
    handle: TextureHandle,
  ): { width: number; height: number } | null {
    const resource = this.textures.get(handle);
    return resource ? { width: resource.width, height: resource.height } : null;
  }

  getSwapChainFormat(): import("../types").TextureFormat {
    // Return the GPU preferred format (bgra8unorm on most platforms)
    return this.presentationFormat as import("../types").TextureFormat;
  }

  // ============================================
  // Compute Shaders
  // ============================================

  get supportsCompute(): boolean {
    return true; // WebGPU always supports compute
  }

  createComputeShader(source: string): ShaderHandle {
    const handle = this.handleCounters.shader++;

    const module = this.device.createShaderModule({
      code: source,
    });

    this.shaders.set(handle, { module });

    return handle;
  }

  createComputePipeline(
    desc: ComputePipelineDescriptor,
  ): ComputePipelineHandle {
    const handle = this.handleCounters.computePipeline++;

    const shader = this.shaders.get(desc.shader);
    if (!shader) throw new Error("Invalid shader handle");

    // Create bind group layout for compute (group 0)
    // Layout:
    //   binding 0: uniforms
    //   binding 1-3: storage buffers (first 3)
    //   binding 4: vfxVariables (second uniform)
    //   binding 5+: additional storage buffers
    const storageCount = desc.storageBufferCount ?? 3;
    const textureCount = desc.textureCount ?? 0;

    const entries: GPUBindGroupLayoutEntry[] = [
      // Uniform buffer at binding 0
      {
        binding: 0,
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: "uniform" },
      },
    ];

    // Add first 3 storage buffers (bindings 1, 2, 3)
    const firstBatch = Math.min(storageCount, 3);
    for (let i = 0; i < firstBatch; i++) {
      entries.push({
        binding: i + 1,
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: "storage" },
      });
    }

    // Add vfxVariables uniform at binding 4
    entries.push({
      binding: 4,
      visibility: GPUShaderStage.COMPUTE,
      buffer: { type: "uniform" },
    });

    // Add additional storage buffers at binding 5+
    for (let i = 3; i < storageCount; i++) {
      entries.push({
        binding: i + 2, // 3->5, 4->6, etc.
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: "storage" },
      });
    }

    const bindGroupLayout = this.device.createBindGroupLayout({ entries });

    // Create texture bind group layout (group 1) if textures are used
    let textureBindGroupLayout: GPUBindGroupLayout | undefined;
    const bindGroupLayouts: GPUBindGroupLayout[] = [bindGroupLayout];
    
    if (textureCount > 0) {
      const textureEntries: GPUBindGroupLayoutEntry[] = [];
      for (let i = 0; i < textureCount; i++) {
        textureEntries.push({
          binding: i,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "float" },
        });
      }
      textureBindGroupLayout = this.device.createBindGroupLayout({ entries: textureEntries });
      bindGroupLayouts.push(textureBindGroupLayout);
    }

    const pipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts,
    });

    const pipeline = this.device.createComputePipeline({
      layout: pipelineLayout,
      compute: {
        module: shader.module,
        entryPoint: desc.entryPoint ?? "main",
        constants: desc.constants
          ? Object.fromEntries(
              Object.entries(desc.constants).map(([k, v]) => [k, typeof v === 'boolean' ? (v ? 1 : 0) : v]),
            )
          : undefined,
      },
      label: desc.label,
    });

    this.computePipelines.set(handle, {
      pipeline,
      bindGroupLayout,
      textureBindGroupLayout,
      workgroupSize: desc.workgroupSize ?? [64, 1, 1],
      storageBufferCount: storageCount,
      textureCount,
    });

    return handle;
  }

  destroyComputePipeline(handle: ComputePipelineHandle): void {
    this.computePipelines.delete(handle);
  }

  setComputePipeline(handle: ComputePipelineHandle): void {
    this.ensureComputePass();

    const resource = this.computePipelines.get(handle);
    if (!resource || !this.computePassEncoder) return;

    this.computePassEncoder.setPipeline(resource.pipeline);
    this.currentComputePipeline = handle;
  }

  bindComputeResources(bindings: ComputeBindGroup): void {
    if (
      !this.computePassEncoder ||
      this.currentComputePipeline === NULL_HANDLE
    ) {
      return;
    }

    const pipelineResource = this.computePipelines.get(
      this.currentComputePipeline,
    );
    if (!pipelineResource) {
      return;
    }

    const entries: GPUBindGroupEntry[] = [];
    const storageCount = pipelineResource.storageBufferCount;

    // Uniform buffer (binding 0)
    if (bindings.uniforms && bindings.uniforms.length > 0) {
      const uniformBuffer = this.buffers.get(bindings.uniforms[0]);
      if (uniformBuffer) {
        entries.push({
          binding: 0,
          resource: { buffer: uniformBuffer.gpuBuffer },
        });
      }
    } else {
      entries.push({
        binding: 0,
        resource: { buffer: this.getSharedUniformBuffer() },
      });
    }

    // Storage buffers:
    //   indices 0-2 -> bindings 1, 2, 3
    //   indices 3+  -> bindings 5, 6, 7... (binding 4 is reserved for vfxVariables)
    const firstBatch = Math.min(storageCount, 3);
    for (let i = 0; i < firstBatch; i++) {
      const sb = bindings.storageBuffers?.[i];
      if (sb) {
        const buffer = this.buffers.get(sb.buffer);
        if (buffer) {
          entries.push({
            binding: i + 1,
            resource: {
              buffer: buffer.gpuBuffer,
              offset: sb.offset ?? 0,
              size: sb.size ?? buffer.size,
            },
          });
          continue;
        }
      }
      // Use dummy buffer for unfilled slot
      entries.push({
        binding: i + 1,
        resource: { buffer: this.dummyStorageBuffers[i] },
      });
    }

    // Uniform buffer at binding 4 (vfxVariables)
    if (bindings.uniforms && bindings.uniforms.length > 1) {
      const variableBuffer = this.buffers.get(bindings.uniforms[1]);
      if (variableBuffer) {
        entries.push({
          binding: 4,
          resource: { buffer: variableBuffer.gpuBuffer },
        });
      } else {
        entries.push({
          binding: 4,
          resource: { buffer: this.getSharedUniformBuffer() },
        });
      }
    } else {
      entries.push({
        binding: 4,
        resource: { buffer: this.getSharedUniformBuffer() },
      });
    }

    // Additional storage buffers at binding 5+
    for (let i = 3; i < storageCount; i++) {
      const sb = bindings.storageBuffers?.[i];
      if (sb) {
        const buffer = this.buffers.get(sb.buffer);
        if (buffer) {
          entries.push({
            binding: i + 2, // 3->5, 4->6, etc.
            resource: {
              buffer: buffer.gpuBuffer,
              offset: sb.offset ?? 0,
              size: sb.size ?? buffer.size,
            },
          });
          continue;
        }
      }
      // Create temporary dummy buffer for unfilled additional slots
      entries.push({
        binding: i + 2,
        resource: { buffer: this.dummyStorageBuffers[0] },
      });
    }

    const bindGroup = this._makeBindGroup({
      layout: pipelineResource.bindGroupLayout,
      entries,
    });

    this.computePassEncoder.setBindGroup(0, bindGroup);

    // Bind textures (group 1) if the pipeline uses textures
    if (pipelineResource.textureCount > 0 && pipelineResource.textureBindGroupLayout) {
      const textureEntries: GPUBindGroupEntry[] = [];
      for (let i = 0; i < pipelineResource.textureCount; i++) {
        const textureHandle = bindings.textures?.[i];
        if (textureHandle !== undefined) {
          const textureResource = this.textures.get(textureHandle);
          if (textureResource) {
            textureEntries.push({
              binding: i,
              resource: textureResource.gpuView,
            });
            continue;
          }
        }
        // Use dummy texture for unfilled slot
        textureEntries.push({
          binding: i,
          resource: this.dummyTexture!.gpuView,
        });
      }
      
      const textureBindGroup = this._makeBindGroup({
        layout: pipelineResource.textureBindGroupLayout,
        entries: textureEntries,
      });
      
      this.computePassEncoder.setBindGroup(1, textureBindGroup);
    }
  }

  dispatchCompute(size: DispatchSize): void {
    if (!this.computePassEncoder) return;

    this.computePassEncoder.dispatchWorkgroups(
      size.x,
      size.y ?? 1,
      size.z ?? 1,
    );
    this.stats.computeDispatches++;
  }

  dispatchComputeIndirect(buffer: BufferHandle, offset = 0): void {
    if (!this.computePassEncoder) return;

    const bufferResource = this.buffers.get(buffer);
    if (!bufferResource) return;

    this.computePassEncoder.dispatchWorkgroupsIndirect(
      bufferResource.gpuBuffer,
      offset,
    );
    this.stats.computeDispatches++;
  }

  computeBarrier(): void {
    // In WebGPU, we end the compute pass to create a barrier
    this.endComputePass();
  }

  private ensureComputePass(): void {
    if (this.computePassEncoder) return;

    // End render pass if active
    if (this.renderPassEncoder) {
      this.renderPassEncoder.end();
      this.renderPassEncoder = null;
    }

    if (!this.commandEncoder) {
      this.commandEncoder = this.device.createCommandEncoder({ label: 'ce:compute' });
    }

    this.computePassEncoder = this.commandEncoder.beginComputePass({
      label: "compute-pass",
      timestampWrites: this.gpuTimestamps(),
    });
  }

  private endComputePass(): void {
    if (this.computePassEncoder) {
      this.computePassEncoder.end();
      this.computePassEncoder = null;
      this.currentComputePipeline = NULL_HANDLE;
    }
  }

  /**
   * Flush any pending compute work by submitting the current command buffer.
   * Call this after compute dispatches if you need data available before render pass.
   * This ensures compute results are visible to subsequent operations.
   */
  flushCompute(): void {
    // End compute pass if active
    this.endComputePass();

    // End any active render pass
    if (this.renderPassEncoder) {
      this.renderPassEncoder.end();
      this.renderPassEncoder = null;
    }

    // Submit and reset command encoder
    if (this.commandEncoder) {
      const commandBuffer = this.commandEncoder.finish();
      this.stats.queueSubmits++;
      this.device.queue.submit([commandBuffer]);
      this.commandEncoder = null;
    }
  }

  // ============================================
  // Debug Markers (for PIX, RenderDoc, etc.)
  // ============================================

  /**
   * Push a debug group. All subsequent commands will be nested under this label.
   * Must be paired with popDebugGroup().
   */
  pushDebugGroup(label: string): void {
    if (this.computePassEncoder) {
      this.computePassEncoder.pushDebugGroup(label);
    } else if (this.renderPassEncoder) {
      this.renderPassEncoder.pushDebugGroup(label);
    } else if (this.commandEncoder) {
      this.commandEncoder.pushDebugGroup(label);
    }
    // If no encoder is active, skip silently - debug markers are optional
  }

  /**
   * Pop the current debug group.
   */
  popDebugGroup(): void {
    if (this.computePassEncoder) {
      this.computePassEncoder.popDebugGroup();
    } else if (this.renderPassEncoder) {
      this.renderPassEncoder.popDebugGroup();
    } else if (this.commandEncoder) {
      this.commandEncoder.popDebugGroup();
    }
  }

  /**
   * Insert a debug marker at the current point.
   */
  insertDebugMarker(label: string): void {
    if (this.computePassEncoder) {
      this.computePassEncoder.insertDebugMarker(label);
    } else if (this.renderPassEncoder) {
      this.renderPassEncoder.insertDebugMarker(label);
    } else if (this.commandEncoder) {
      this.commandEncoder.insertDebugMarker(label);
    }
    // If no encoder is active, skip silently - debug markers are optional
  }

  // ============================================
  // Format Mapping Helpers
  // ============================================

  /** Get bytes per pixel for a given texture format */
  private getBytesPerPixel(format: GPUTextureFormat): number {
    switch (format) {
      case 'r8unorm':
        return 1;
      case 'rg8unorm':
        return 2;
      case 'rgba8unorm':
      case 'rgba8unorm-srgb':
      case 'bgra8unorm':
      case 'depth24plus':
      case 'depth32float':
        return 4;
      case 'rgba16float':
        return 8;
      case 'rgba32float':
        return 16;
      default:
        return 4;
    }
  }

  private mapTextureFormat(format: string): GPUTextureFormat {
    switch (format) {
      case "rgba8unorm":
        return "rgba8unorm";
      case "rgba8unorm-srgb":
        return "rgba8unorm-srgb";
      case "bgra8unorm":
        return "bgra8unorm";
      case "r8unorm":
        return "r8unorm";
      case "rg8unorm":
        return "rg8unorm";
      case "rgba16float":
        return "rgba16float";
      case "rgba32float":
        return "rgba32float";
      case "depth24plus":
        return "depth24plus";
      case "depth32float":
        return "depth32float";
      default:
        return "rgba8unorm";
    }
  }

  private mapTextureUsage(usage: string[]): GPUTextureUsageFlags {
    let flags = 0;
    for (const u of usage) {
      switch (u) {
        case "sampled":
          flags |= GPUTextureUsage.TEXTURE_BINDING;
          break;
        case "storage":
          flags |= GPUTextureUsage.STORAGE_BINDING;
          break;
        case "render-target":
          flags |= GPUTextureUsage.RENDER_ATTACHMENT;
          break;
        case "copy-src":
          flags |= GPUTextureUsage.COPY_SRC;
          break;
        case "copy-dst":
          flags |= GPUTextureUsage.COPY_DST;
          break;
      }
    }
    return flags;
  }

  private mapBufferUsage(usage: string[]): GPUBufferUsageFlags {
    let flags = 0;
    for (const u of usage) {
      switch (u) {
        case "vertex":
          flags |= GPUBufferUsage.VERTEX;
          break;
        case "index":
          flags |= GPUBufferUsage.INDEX;
          break;
        case "uniform":
          flags |= GPUBufferUsage.UNIFORM;
          break;
        case "storage":
          flags |= GPUBufferUsage.STORAGE;
          break;
        case "copy-src":
          flags |= GPUBufferUsage.COPY_SRC;
          break;
        case "copy-dst":
          flags |= GPUBufferUsage.COPY_DST;
          break;
        case "indirect":
          flags |= GPUBufferUsage.INDIRECT;
          break;
      }
    }
    // Always add COPY_DST for dynamic updates
    flags |= GPUBufferUsage.COPY_DST;
    return flags;
  }

  private mapWrapMode(wrap: string): GPUAddressMode {
    switch (wrap) {
      case "repeat":
        return "repeat";
      case "mirror":
        return "mirror-repeat";
      default:
        return "clamp-to-edge";
    }
  }

  private mapVertexFormat(format: string): GPUVertexFormat {
    switch (format) {
      case "float32":
        return "float32";
      case "float32x2":
        return "float32x2";
      case "float32x3":
        return "float32x3";
      case "float32x4":
        return "float32x4";
      case "uint8x4":
        return "uint8x4";
      case "unorm8x4":
        return "unorm8x4";
      case "sint8x4":
        return "sint8x4";
      case "snorm8x4":
        return "snorm8x4";
      case "uint16x2":
        return "uint16x2";
      case "uint16x4":
        return "uint16x4";
      case "sint16x2":
        return "sint16x2";
      case "sint16x4":
        return "sint16x4";
      case "uint32":
        return "uint32";
      case "uint32x2":
        return "uint32x2";
      case "uint32x3":
        return "uint32x3";
      case "uint32x4":
        return "uint32x4";
      case "float16x2":
        return "float16x2";
      case "float16x4":
        return "float16x4";
      default:
        return "float32x4";
    }
  }

  private mapBlendFactor(factor: string): GPUBlendFactor {
    switch (factor) {
      case "zero":
        return "zero";
      case "one":
        return "one";
      case "src":
        return "src";
      case "one-minus-src":
        return "one-minus-src";
      case "src-alpha":
        return "src-alpha";
      case "one-minus-src-alpha":
        return "one-minus-src-alpha";
      case "dst":
        return "dst";
      case "one-minus-dst":
        return "one-minus-dst";
      case "dst-alpha":
        return "dst-alpha";
      case "one-minus-dst-alpha":
        return "one-minus-dst-alpha";
      default:
        return "one";
    }
  }

  private mapBlendOp(op: string): GPUBlendOperation {
    switch (op) {
      case "add":
        return "add";
      case "subtract":
        return "subtract";
      case "reverse-subtract":
        return "reverse-subtract";
      case "min":
        return "min";
      case "max":
        return "max";
      default:
        return "add";
    }
  }

  private mapTopology(topology: string): GPUPrimitiveTopology {
    switch (topology) {
      case "triangle-list":
        return "triangle-list";
      case "triangle-strip":
        return "triangle-strip";
      case "line-list":
        return "line-list";
      case "line-strip":
        return "line-strip";
      case "point-list":
        return "point-list";
      default:
        return "triangle-list";
    }
  }

  private mapCullMode(cull: string): GPUCullMode {
    switch (cull) {
      case "none":
        return "none";
      case "front":
        return "front";
      case "back":
        return "back";
      default:
        return "none";
    }
  }

  private mapCompareFunction(compare: string): GPUCompareFunction {
    switch (compare) {
      case "never":
        return "never";
      case "less":
        return "less";
      case "equal":
        return "equal";
      case "less-equal":
        return "less-equal";
      case "greater":
        return "greater";
      case "not-equal":
        return "not-equal";
      case "greater-equal":
        return "greater-equal";
      case "always":
        return "always";
      default:
        return "always";
    }
  }
}
